import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "../lib/useReducedMotion";

/**
 * Radial cyan spotlight that follows the cursor smoothly across the hero.
 * Pure transform-based, GPU-accelerated. Disabled on touch / reduced-motion.
 *
 * Wrap the spotlight inside the element you want it to track (must be
 * `position: relative`). It absolute-positions itself and follows the local
 * mouse coordinates.
 */
export const SpotlightCursor = ({
    color = "rgba(3, 216, 243, 0.18)",
    size = 420,
    ease = 0.15,
    className = "",
}) => {
    const ref = useRef(null);
    const stateRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, raf: null });
    const reduced = useReducedMotion();
    const touch =
        typeof window !== "undefined" && window.matchMedia
            ? window.matchMedia("(hover: none)").matches
            : false;

    useEffect(() => {
        if (reduced || touch || !ref.current) return;
        const parent = ref.current.parentElement;
        if (!parent) return;
        const state = stateRef.current;

        const onMove = (e) => {
            const r = parent.getBoundingClientRect();
            const s = state;
            s.tx = e.clientX - r.left;
            s.ty = e.clientY - r.top;
            if (!s.raf) {
                const step = () => {
                    s.x += (s.tx - s.x) * ease;
                    s.y += (s.ty - s.y) * ease;
                    if (ref.current) {
                        ref.current.style.transform = `translate3d(${s.x - size / 2}px, ${
                            s.y - size / 2
                        }px, 0)`;
                    }
                    if (Math.abs(s.tx - s.x) < 0.5 && Math.abs(s.ty - s.y) < 0.5) {
                        s.raf = null;
                        return;
                    }
                    s.raf = requestAnimationFrame(step);
                };
                s.raf = requestAnimationFrame(step);
            }
        };

        const onLeave = () => {
            if (ref.current) ref.current.style.opacity = "0";
        };
        const onEnter = () => {
            if (ref.current) ref.current.style.opacity = "1";
        };

        parent.addEventListener("mousemove", onMove);
        parent.addEventListener("mouseleave", onLeave);
        parent.addEventListener("mouseenter", onEnter);
        return () => {
            parent.removeEventListener("mousemove", onMove);
            parent.removeEventListener("mouseleave", onLeave);
            parent.removeEventListener("mouseenter", onEnter);
            if (state.raf) cancelAnimationFrame(state.raf);
        };
    }, [ease, size, reduced, touch]);

    if (reduced || touch) return null;
    return (
        <div
            ref={ref}
            aria-hidden
            className={`pointer-events-none absolute top-0 left-0 rounded-full opacity-0 transition-opacity duration-300 ${className}`}
            style={{
                width: size,
                height: size,
                background: `radial-gradient(closest-side, ${color}, transparent 70%)`,
                filter: "blur(8px)",
                mixBlendMode: "screen",
            }}
            data-testid="spotlight-cursor"
        />
    );
};

export default SpotlightCursor;
